import { useEffect, useMemo, useState } from "react";
import { Card, SectionHeader } from "../components/Card";
import { Button } from "../components/Button";
import { Input } from "../components/Input";
import { useAuth } from "../lib/auth.jsx";

function isActive(status = "") {
  return ["waiting", "called"].includes(String(status).toLowerCase());
}

function minutesSince(value) {
  if (!value) return null;
  const minutes = Math.max(0, Math.floor((Date.now() - new Date(value).getTime()) / 60_000));
  return Number.isFinite(minutes) ? minutes : null;
}

function formatQueueAge(value) {
  const minutes = minutesSince(value);
  if (minutes == null) return "";
  if (minutes < 60) return `waiting ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  const mins = minutes % 60;
  if (hours < 24) return `waiting ${hours} hr${hours === 1 ? "" : "s"}${mins ? ` ${mins} min` : ""}`;
  const days = Math.floor(hours / 24);
  return `waiting ${days} day${days === 1 ? "" : "s"}`;
}

function formatJoinedAt(value) {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
}

export function WalkinsScreen() {
  const { authedFetch, profile } = useAuth();
  const [queue, setQueue] = useState([]);
  const [myEntry, setMyEntry] = useState(null);
  const [desiredService, setDesiredService] = useState("");
  const [notes, setNotes] = useState("");
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState("");
  const [err, setErr] = useState("");

  const isStaff = ["technician", "admin_tech"].includes(profile?.role || "");
  const activeQueue = useMemo(() => queue.filter((entry) => isActive(entry.status)), [queue]);

  async function refresh() {
    setLoading(true);
    setErr("");
    try {
      if (isStaff) {
        const out = await authedFetch("/api/ops/walkin");
        setQueue(out.queue || []);
      } else {
        const out = await authedFetch("/api/ops/walkin/me");
        setMyEntry(out.entry || null);
      }
    } catch (e) {
      setErr(e.message);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => { refresh(); }, [isStaff]);

  useEffect(() => {
    if (!isStaff) return undefined;
    const id = setInterval(refresh, 30_000);
    return () => clearInterval(id);
  }, [isStaff]);

  async function joinQueue(event) {
    event.preventDefault();
    setBusy("join");
    setErr("");
    try {
      const out = await authedFetch("/api/ops/walkin/join", {
        method: "POST",
        body: JSON.stringify({
          desiredService: desiredService.trim() || null,
          notes: notes.trim() || null,
        }),
      });
      setMyEntry(out.entry || null);
      setDesiredService("");
      setNotes("");
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy("");
    }
  }

  async function leaveQueue() {
    setBusy("leave");
    setErr("");
    try {
      await authedFetch("/api/ops/walkin/me", { method: "DELETE" });
      setMyEntry(null);
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy("");
    }
  }

  async function updateEntry(entry, status) {
    setBusy(`${entry.id}-${status}`);
    setErr("");
    try {
      await authedFetch(`/api/ops/walkin/${entry.id}`, {
        method: "PATCH",
        body: JSON.stringify({ status }),
      });
      await refresh();
    } catch (e) {
      setErr(e.message);
    } finally {
      setBusy("");
    }
  }

  return (
    <div style={{ minHeight: "100vh", background: "var(--bg)", paddingBottom: 80 }}>
      <div style={{ maxWidth: 980, margin: "0 auto", padding: "30px 16px" }}>
        <SectionHeader
          title="Walk-in Queue"
          subtitle={isStaff ? "Call, serve, or clear waiting walk-ins." : "Join the studio queue without a scheduled appointment."}
        />

        {err && <div role="alert" style={alertStyle()}>{err}</div>}

        {isStaff ? (
          <StaffQueue
            queue={activeQueue}
            loading={loading}
            busy={busy}
            onRefresh={refresh}
            onUpdate={updateEntry}
          />
        ) : (
          <CustomerQueue
            entry={myEntry}
            loading={loading}
            busy={busy}
            desiredService={desiredService}
            notes={notes}
            onDesiredService={setDesiredService}
            onNotes={setNotes}
            onJoin={joinQueue}
            onLeave={leaveQueue}
          />
        )}
      </div>
    </div>
  );
}

function CustomerQueue({
  entry,
  loading,
  busy,
  desiredService,
  notes,
  onDesiredService,
  onNotes,
  onJoin,
  onLeave,
}) {
  if (loading) return <Card style={{ maxWidth: 620 }}><div style={emptyStyle()}>Checking queue...</div></Card>;

  if (entry && isActive(entry.status)) {
	    const waited = formatQueueAge(entry.joined_at);
	    const joined = formatJoinedAt(entry.joined_at);
    const called = entry.status === "called";
    return (
      <Card style={{ maxWidth: 620, textAlign: "center" }}>
        <div style={{
          width: 132,
          height: 132,
          borderRadius: "50%",
          display: "grid",
          placeItems: "center",
          margin: "4px auto 22px",
          color: called ? "#111" : "#fff",
          background: called ? "linear-gradient(135deg, var(--gold), #9A6A10)" : "linear-gradient(135deg, var(--mauve), var(--mauve-deep))",
          boxShadow: "var(--shadow-lg)",
        }}>
          <div>
            <div style={{ fontSize: 42, fontWeight: 900, lineHeight: 1 }}>#{entry.position}</div>
            <div style={{ fontSize: 12, fontWeight: 800, opacity: 0.78 }}>in queue</div>
          </div>
        </div>
        <h3 style={{ margin: 0, color: "var(--text)", fontFamily: "var(--font-display)", fontSize: 26 }}>
          {called ? "You have been called" : "You are in the queue"}
        </h3>
        <p style={{ color: "var(--text-muted)", lineHeight: 1.55 }}>
          {called ? "Please make your way to the front desk." : "We will notify you when a technician is ready."}
        </p>
        {entry.desired_service && (
          <div style={{
            display: "inline-flex",
            padding: "7px 12px",
            borderRadius: 999,
            color: "var(--text-accent)",
            background: "rgba(196,135,154,0.12)",
            border: "1px solid var(--border)",
            fontSize: 13,
            fontWeight: 800,
            marginBottom: 12,
          }}>
            {entry.desired_service}
          </div>
        )}
	        {(waited || joined) && (
	          <div style={{ color: "var(--text-muted)", fontSize: 12, marginBottom: 20 }}>
	            {waited ? waited[0].toUpperCase() + waited.slice(1) : "Waiting"}
	            {joined ? ` · joined ${joined}` : ""}
	          </div>
	        )}
        <Button label="Leave queue" variant="outline" onClick={onLeave} loading={busy === "leave"} full />
      </Card>
    );
  }

  return (
    <Card as="form" onSubmit={onJoin} style={{ maxWidth: 620 }}>
      <div style={{ display: "grid", gap: 14 }}>
        <Input
          label="Service interest"
          value={desiredService}
          onChange={(event) => onDesiredService(event.target.value)}
          placeholder="Gel manicure, acrylic set, nail repair"
        />
        <Input
          label="Notes"
          value={notes}
          onChange={(event) => onNotes(event.target.value)}
          placeholder="Allergies, nail condition, or requests"
        />
        <Button type="submit" label="Join queue" variant="primary" loading={busy === "join"} full />
      </div>
    </Card>
  );
}

function StaffQueue({ queue, loading, busy, onRefresh, onUpdate }) {
  return (
    <Card style={{ maxWidth: "none", padding: 0, overflow: "hidden" }}>
      <div style={{ padding: 18, display: "flex", justifyContent: "space-between", gap: 12, flexWrap: "wrap", borderBottom: "1px solid var(--border)" }}>
        <div>
          <h3 style={{ margin: 0, color: "var(--text)", fontFamily: "var(--font-display)", fontSize: 22 }}>Current queue</h3>
          <p style={{ margin: "4px 0 0", color: "var(--text-muted)", fontSize: 13 }}>{queue.length} active walk-ins</p>
        </div>
        <Button size="sm" variant="outline" label="Refresh" onClick={onRefresh} loading={loading} />
      </div>

      {loading ? (
        <div style={emptyStyle()}>Loading queue...</div>
      ) : queue.length === 0 ? (
        <div style={emptyStyle()}>Queue is clear.</div>
      ) : (
        <div style={{ display: "grid" }}>
          {queue.map((entry) => {
            const called = entry.status === "called";
	            const waited = formatQueueAge(entry.joined_at);
	            const joined = formatJoinedAt(entry.joined_at);
            return (
              <article key={entry.id} style={{
                display: "grid",
                gridTemplateColumns: "auto minmax(0, 1fr) auto",
                gap: 14,
                alignItems: "center",
                padding: 16,
                borderBottom: "1px solid var(--border)",
                background: called ? "rgba(201,168,76,0.08)" : "transparent",
              }}>
                <div style={{
                  width: 48,
                  height: 48,
                  borderRadius: "50%",
                  display: "grid",
                  placeItems: "center",
                  background: called ? "var(--gold)" : "rgba(196,135,154,0.16)",
                  color: called ? "#111" : "var(--text-accent)",
                  fontWeight: 900,
                }}>
                  #{entry.position}
                </div>
                <div style={{ minWidth: 0 }}>
                  <strong style={{ display: "block", color: "var(--text)", fontSize: 14 }}>
                    {entry.customer?.full_name || entry.customer?.email || "Walk-in"}
                  </strong>
	                  <div style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 4 }}>
	                    {[entry.desired_service, waited, joined ? `joined ${joined}` : null].filter(Boolean).join(" | ")}
	                  </div>
                  {entry.notes && <div style={{ color: "var(--text-muted)", fontSize: 12, marginTop: 5 }}>{entry.notes}</div>}
                </div>
                <div style={{ display: "flex", gap: 8, flexWrap: "wrap", justifyContent: "flex-end" }}>
                  {!called && (
                    <Button size="sm" label="Call" onClick={() => onUpdate(entry, "called")} loading={busy === `${entry.id}-called`} />
                  )}
                  <Button
                    size="sm"
                    variant={called ? "gold" : "outline"}
                    label={called ? "Served" : "Skip"}
                    onClick={() => onUpdate(entry, called ? "served" : "left")}
                    loading={busy === `${entry.id}-${called ? "served" : "left"}`}
                  />
                </div>
              </article>
            );
          })}
        </div>
      )}
    </Card>
  );
}

function alertStyle() {
  return {
    background: "rgba(180, 50, 50, 0.10)",
    color: "#B42318",
    border: "1px solid rgba(180, 50, 50, 0.22)",
    borderRadius: 14,
    padding: 12,
    marginBottom: 16,
    fontWeight: 700,
  };
}

function emptyStyle() {
  return { padding: 32, textAlign: "center", color: "var(--text-muted)" };
}
